import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheckIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { useRetentionConsent } from '@/hooks/useRewards'
import type { RetentionConsent } from '@/types'

export default function RetentionConsentPage() {
  const { t } = useTranslation('rewards')
  const { t: tc } = useTranslation('common')
  const { mutate: giveConsent, isPending } = useRetentionConsent()
  const [result, setResult] = useState<RetentionConsent | null>(null)
  const [consentError, setConsentError] = useState<string | null>(null)

  const handleOptIn = () => {
    setConsentError(null)
    giveConsent(undefined, {
      onSuccess: (data) => {
        setResult(data)
      },
      onError: (err) => {
        const apiError = (err as { response?: { data?: { error?: string } } }).response?.data
          ?.error
        if (apiError === 'already_registered') {
          // Treat as success — already enrolled
          setResult({ consent_given_at: new Date().toISOString(), consent_source: 'liff' })
        } else {
          setConsentError(t('errors.consent_failed'))
        }
      },
    })
  }

  // Success / already-enrolled state
  if (result) {
    return (
      <div className="p-6 flex flex-col items-center gap-5 text-center">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
          <CheckCircleIcon className="w-8 h-8 text-success" />
        </div>
        <div className="space-y-1">
          <p className="text-base font-bold text-base-content">{t('consent_success')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-5">
      {/* Icon header */}
      <div className="flex flex-col items-center gap-3 pt-4 text-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
          <ShieldCheckIcon className="w-7 h-7 text-primary" />
        </div>
        <h1 className="text-base font-bold text-base-content">{t('consent_title')}</h1>
      </div>

      {/* Explanation */}
      <div className="flat-card p-4 space-y-3">
        <p className="text-sm text-base-content/80 leading-relaxed">{t('consent_body')}</p>
        <div className="border-t border-base-200 pt-3 flex items-start gap-2">
          <ShieldCheckIcon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-base-content/60">{t('consent_data_shared')}</p>
        </div>
      </div>

      {/* Error */}
      {consentError && (
        <div className="alert alert-error text-sm">{consentError}</div>
      )}

      {/* Explicit opt-in button */}
      <button
        className="btn btn-primary w-full"
        onClick={handleOptIn}
        disabled={isPending}
        aria-label={t('consent_opt_in')}
      >
        {isPending ? tc('status.loading') : t('consent_opt_in')}
      </button>

      <p className="text-xs text-base-content/40 text-center px-4">
        {/* Brief legal note — no cookie/tracking jargon needed; consent is explicit */}
        {t('consent_data_shared')}
      </p>
    </div>
  )
}
